// 데스크탑 우측 하단 — 일별 출퇴근 리스트 패널 (Phase 1-2 후속 v2)
//   직원 미선택: 전체 직원 (직원명 표시) — 날짜 내림차순
//   직원 선택  : 그 직원만 — 날짜 내림차순
//   행 클릭 → 수정 모달 (onEdit)
//   10개씩 페이지네이션
//   행 형식: [날짜] [출근·퇴근] [색점 상태] [근무시간]  (모바일 펼침 행과 동일 패턴)
import React, { useState, useEffect, useMemo } from 'react';
import {
  getRowTotalMinutes, getRowBreakMinutes, formatMinutesKorean, matchesAttendanceFilter,
  getOverrideStatus,
} from './utils';
import { btn } from './styles';

const PAGE_SIZE = 10;

const dotStyle = (color) => ({
  width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0, display: 'inline-block',
});

/**
 * Props:
 *   groupedByUser  — useAttendanceData.groupedByUser
 *   selectedUserId — 선택된 직원 (없으면 전체)
 *   onEdit(row)    — 행 클릭 콜백
 */
export function AttendanceListPanel({ groupedByUser, selectedUserId, onEdit }) {
  // 표시할 행 목록 — 직원 선택 시 그 직원만 / 미선택 시 전체 (날짜 내림차순)
  const allRows = useMemo(() => {
    const out = [];
    Object.values(groupedByUser || {}).forEach((g) => {
      if (selectedUserId && String(g.user.id) !== String(selectedUserId)) return;
      g.rows.forEach((r) => out.push({ ...r, _userName: g.user.name, _userCompany: g.user.company_name || '' }));
    });
    return out.sort((a, b) => String(b.date_ymd || '').localeCompare(String(a.date_ymd || '')));
  }, [groupedByUser, selectedUserId]);

  // 페이지네이션 — 직원 변경 시 1페이지로
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(allRows.length / PAGE_SIZE));
  useEffect(() => { setPage(1); }, [selectedUserId, allRows.length]);
  const safePage = Math.min(page, totalPages);
  const pageRows = allRows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  return (
    <div style={panelStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <strong style={{ fontSize: 13 }}>출퇴근 내역</strong>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          {selectedUserId ? '(직원 선택됨)' : `(전체 ${allRows.length}건)`}
        </span>
      </div>
      <div style={{ display: 'grid', gap: 4 }}>
        {pageRows.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', padding: 12 }}>데이터 없음</div>
        )}
        {pageRows.map((row) => {
          const ov           = getOverrideStatus(row);                  // 예외 스케줄
          const isAbsence    = ov?.kind === 'absence';
          const isLate       = !isAbsence && matchesAttendanceFilter(row, 'late');
          const isEarly      = !isAbsence && matchesAttendanceFilter(row, 'early');
          const isMissingOut = !isAbsence && matchesAttendanceFilter(row, 'missing_out');
          const isDayoff     = !isAbsence && matchesAttendanceFilter(row, 'dayoff');
          const total        = getRowTotalMinutes(row);
          const breakMin     = getRowBreakMinutes(row);
          const workTime     = total === null ? '-' : formatMinutesKorean(Math.max(0, total - (breakMin || 0)));
          const rowKey       = row.id != null ? `l-${row.id}` : `l-ov-${row.user_id}-${row.date_ymd}`;
          return (
            <div
              key={rowKey}
              onClick={() => row.id != null && onEdit && onEdit(row)}
              style={{ ...rowCardStyle, cursor: row.id != null ? 'pointer' : 'default' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                {/* 직원 미선택 시 직원명 표시 */}
                {!selectedUserId && (
                  <span style={{ width: 60, fontWeight: 600, color: 'var(--text)', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {row._userName}
                  </span>
                )}
                <span style={{ width: 50, color: 'var(--text-sub)', flexShrink: 0 }}>
                  {String(row.date_ymd || '').slice(5) || '-'}
                </span>
                {/* 출퇴근 — 예외 있으면 라벨로 대체 (휴무: 시간 무시 / 시간변경: 변경시간 + 실제 작게) */}
                {ov ? (
                  <span className={`${ov.color.bg} ${ov.color.text}`} style={{ padding: '1px 6px', borderRadius: 4, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {ov.label}
                    {ov.kind === 'modified' && row.check_in_hhmm && (
                      <span style={{ fontSize: 10, fontWeight: 400, opacity: 0.7, marginLeft: 4 }}>실제 {row.check_in_hhmm}</span>
                    )}
                  </span>
                ) : (
                  <span style={{ color: 'var(--text)', whiteSpace: 'nowrap' }}>
                    {row.check_in_hhmm || '-'}<span style={{ color: 'var(--text-muted)' }}>·</span>{row.check_out_hhmm || (row.check_in ? <span style={{ color: '#dc2626' }}>미체크</span> : '-')}
                  </span>
                )}
                {isLate       && <span title={`지각 ${Number(row.late_minutes)}분`} style={dotStyle('#f59e0b')} />}
                {isEarly      && <span title={`조퇴 ${Number(row.early_leave_minutes)}분`} style={dotStyle('#fb923c')} />}
                {isMissingOut && <span title="퇴근미체크" style={dotStyle('#ef4444')} />}
                {isDayoff     && <span title="휴무" style={dotStyle('#94a3b8')} />}
                {row.is_offday_work && <span title="휴무근무" style={dotStyle('#15803d')} />}
                {/* 사유 텍스트 (정상은 빈 칸) — 색상은 점과 동일. 예외(ov) 있으면 위 라벨로 갈음. */}
                <span style={{ fontSize: 11, fontWeight: 600, flexShrink: 0, minWidth: 70, textAlign: 'left' }}>
                  {isLate       && <span style={{ color: '#f59e0b' }}>지각 {Number(row.late_minutes)}분</span>}
                  {isEarly      && <span style={{ color: '#fb923c' }}>{isLate ? ' · ' : ''}조퇴 {Number(row.early_leave_minutes)}분</span>}
                  {isMissingOut && !isLate && !isEarly && <span style={{ color: '#ef4444' }}>퇴근 미체크</span>}
                  {isDayoff && !isLate && !isEarly && !isMissingOut && <span style={{ color: '#94a3b8' }}>휴무</span>}
                  {row.is_offday_work && !isLate && !isEarly && !isMissingOut && !isDayoff && <span style={{ color: '#15803d' }}>휴무근무</span>}
                </span>
                <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-muted)', flexShrink: 0 }}>{workTime}</span>
              </div>
            </div>
          );
        })}
      </div>
      {/* 페이지네이션 — 10개 단위 (marginTop:auto 로 패널 하단 push, 좌측 페이지네이션과 같은 줄) */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, paddingTop: 4, fontSize: 12, marginTop: 'auto' }}>
          <button style={{ ...btn, padding: '2px 10px', fontSize: 12 }} disabled={safePage <= 1} onClick={() => setPage(safePage - 1)}>이전</button>
          <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>{safePage} / {totalPages}</span>
          <button style={{ ...btn, padding: '2px 10px', fontSize: 12 }} disabled={safePage >= totalPages} onClick={() => setPage(safePage + 1)}>다음</button>
        </div>
      )}
    </div>
  );
}

// flex column + height 100% — 페이지네이션의 marginTop:auto가 정상 작동하도록
const panelStyle = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: 12, display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0, height: '100%', minHeight: 0 };
const rowCardStyle = { padding: '6px 8px', borderRadius: 6, background: 'var(--bg-subtle)', cursor: 'pointer' };
